/**
 * TechHub > Agreement types (step 3, 2026-10-23): the kinds of agreement a client can have, per
 * company (MSA, MSSA, SOW, BAA, NDA to start). Types that "can have addenda" take addenda,
 * supplements and rate changes under them; the rest stand alone. Company admins add, rename and
 * retire; nothing is deleted (agreements may use a type) and a type's short code never changes.
 */
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, type ChAgreementType } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { Loading, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";

export function AgreementTypesPage() {
  const { p, toast } = useStore();
  const codes = p.chCodes.filter((c) => p.ch(c).sensitive);
  const [sp, setSp] = useSearchParams();
  const co = codes.includes(sp.get("co") ?? "") ? sp.get("co")! : codes[0];
  const [list, setList] = useState<ChAgreementType[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [nf, setNf] = useState({ code: "", name: "", has_addenda: true });
  const load = async () => { if (co) setList((await api.chAgreementTypes(co)).types); };
  useEffect(() => { setList(null); setEditing(null); load().catch((e: Error) => toast(e.message)); }, [co]);
  if (!co) return <Navigate to="/settings" replace />;
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try { await fn(); toast(msg); await load(); return true; } catch (e) { toast((e as Error).message); return false; } finally { setBusy(false); }
  };
  const active = list?.filter((t) => t.active) ?? [];
  const retired = list?.filter((t) => !t.active) ?? [];
  const okNew = /^[A-Z0-9]{2,8}$/.test(nf.code) && nf.name.trim().length >= 2;

  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="clienthub" />
        <h1 className="title">Agreement types</h1>
        <div className="lede">The kinds of agreement you keep for clients. A type that can have addenda takes addenda, supplements and rate changes under it; the rest stand alone. Each company has its own list.</div>
      </div></div>
      {codes.length > 1 && (
        <div className="ctabs" role="tablist">
          {codes.map((c) => (
            <button key={c} className={`ctab ${c === co ? "on" : ""}`} role="tab" aria-selected={c === co} onClick={() => setSp({ co: c }, { replace: true })}>
              <Tile code={c} /><span>{coName(c)}</span>
            </button>
          ))}
        </div>
      )}
      <div className="card sect">
        <h3>{coName(co)} agreement types</h3>
        <p className="cap">Renaming changes every agreement of that type. Retiring hides it from new agreements; existing ones keep it.</p>
        {!list ? <Loading /> : (
          <>
            <ul className="svc-list">
              {active.map((t) => (
                <li key={t.id} className="agt-row">
                  <span className="agt-code mono">{t.code}</span>
                  {editing === t.id
                    ? <div className="svc-row"><input className="inp" value={name} maxLength={80} autoFocus onChange={(e) => setName(e.target.value)} /></div>
                    : <div className="svc-row"><b>{t.name}</b><span className="svc-desc">{t.has_addenda ? "Can have addenda, supplements and rate changes" : "Stands alone"}</span></div>}
                  <span className="cap">{t.used === 1 ? "1 agreement" : `${t.used ?? 0} agreements`}</span>
                  {editing === t.id
                    ? <><button className="btn btn-accent btn-sm" disabled={busy || name.trim().length < 2} onClick={() => void run(() => api.chUpdateAgreementType(t.id, { name: name.trim() }), "Saved.").then((ok) => ok && setEditing(null))}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button></>
                    : <><button className="btn btn-ghost btn-sm" onClick={() => { setEditing(t.id); setName(t.name); }}>Rename</button>
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run(() => api.chUpdateAgreementType(t.id, { has_addenda: !t.has_addenda }), t.has_addenda ? `${t.code} now stands alone.` : `${t.code} can now have addenda.`)}>{t.has_addenda ? "Make it stand alone" : "Allow addenda"}</button>
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run(() => api.chUpdateAgreementType(t.id, { active: false }), `${t.code} retired.`)}>Retire</button></>}
                </li>
              ))}
              {!active.length && <li className="cap">No types yet. Add the first one below.</li>}
            </ul>
            <div className="form-row" style={{ marginTop: 14, alignItems: "flex-end" }}>
              <label><span className="lbl">Short code</span><input className="inp mono" value={nf.code} maxLength={8} placeholder="SLA" style={{ width: 110 }}
                onChange={(e) => setNf((x) => ({ ...x, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))} /></label>
              <label className="grow"><span className="lbl">Name</span><input className="inp" value={nf.name} maxLength={80} placeholder="e.g. Service Level Agreement" onChange={(e) => setNf((x) => ({ ...x, name: e.target.value }))} /></label>
              <label className="chk agt-chk"><input type="checkbox" checked={nf.has_addenda} onChange={(e) => setNf((x) => ({ ...x, has_addenda: e.target.checked }))} /> Can have addenda</label>
              <button className="btn btn-accent" disabled={busy || !okNew} onClick={() => void run(async () => { await api.chAddAgreementType({ company: co, code: nf.code, name: nf.name.trim(), has_addenda: nf.has_addenda }); setNf({ code: "", name: "", has_addenda: true }); }, `${nf.code} added.`)}>Add type</button>
            </div>
            {retired.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div className="lbl">Retired</div>
                <ul className="svc-list">
                  {retired.map((t) => (
                    <li key={t.id} className="agt-row"><span className="agt-code mono">{t.code}</span><div className="svc-row"><b>{t.name}</b></div><span className="cap">{t.used ?? 0} agreements</span>
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run(() => api.chUpdateAgreementType(t.id, { active: true }), `${t.code} is back in the list.`)}>Bring back</button></li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
